"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { Check, ListPlus, Loader2, LogIn, Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { QuantityControl } from "@/components/ui/quantity-control";
import { cn } from "@/lib/utils";

interface UserList {
  id: string;
  name: string;
  emoji: string | null;
  itemCount: number;
}

interface Props {
  open: boolean;
  onClose: () => void;
  productId: string;
  productName: string;
  soldByWeight?: boolean;
  onAdded?: () => void;
}

const NEW_LIST_EMOJIS = ["📋", "🛒", "🥑", "🍎", "🥖", "🧃", "🧻", "💊", "🎁", "📦"];

type LoadState = "idle" | "loading" | "ready" | "guest" | "disabled" | "error";

export function AddToListModal({ open, onClose, productId, productName, soldByWeight = false, onAdded }: Props) {
  const [lists, setLists] = useState<UserList[]>([]);
  const [loadState, setLoadState] = useState<LoadState>("idle");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newEmoji, setNewEmoji] = useState("📋");

  useEffect(() => {
    if (!open) return;
    setError(null);
    setSuccess(null);
    setSubmitting(null);
    setQuantity(1);
    setCreating(false);
    setNewName("");
    setNewEmoji("📋");
    let cancelled = false;
    setLoadState("loading");
    fetch("/api/profile/lists")
      .then(async (r) => {
        if (cancelled) return;
        if (r.status === 401) {
          setLoadState("guest");
          return;
        }
        if (r.status === 404) {
          setLoadState("disabled");
          return;
        }
        if (!r.ok) {
          setLoadState("error");
          return;
        }
        const j = await r.json();
        setLists(j.data ?? []);
        setLoadState("ready");
      })
      .catch(() => {
        if (!cancelled) setLoadState("error");
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  if (!open) return null;

  async function addToList(listId: string) {
    setSubmitting(listId);
    setError(null);
    try {
      const res = await fetch(`/api/profile/lists/${listId}/items`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: productName, productId, quantity }),
      });
      if (!res.ok) throw new Error("save");
      const list = lists.find((l) => l.id === listId);
      setSuccess(`Añadido a ${list?.name ?? "tu lista"}`);
      setLists((prev) => prev.map((l) => (l.id === listId ? { ...l, itemCount: l.itemCount + 1 } : l)));
      onAdded?.();
      setTimeout(onClose, 900);
    } catch {
      setError("No pudimos guardar el producto. Inténtalo de nuevo.");
    } finally {
      setSubmitting(null);
    }
  }

  async function createAndAdd() {
    if (!newName.trim()) return;
    setSubmitting("__new__");
    setError(null);
    try {
      const res = await fetch("/api/profile/lists", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: newName.trim(), emoji: newEmoji }),
      });
      if (!res.ok) throw new Error("create");
      const j = await res.json();
      const newId = j.data.id as string;
      // Add the product to the freshly created list.
      const addRes = await fetch(`/api/profile/lists/${newId}/items`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: productName, productId, quantity }),
      });
      if (!addRes.ok) throw new Error("save");
      setSuccess(`Lista "${newName.trim()}" creada con el producto`);
      onAdded?.();
      setTimeout(onClose, 1100);
    } catch {
      setError("No pudimos crear la lista.");
    } finally {
      setSubmitting(null);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        className="relative w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl bg-background border shadow-2xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm border-b px-5 py-3 flex items-center gap-2">
          <ListPlus className="w-5 h-5 text-primary" />
          <h3 className="text-base font-bold flex-1">Añadir a una lista</h3>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full hover:bg-muted flex items-center justify-center"
            aria-label="Cerrar"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          <div>
            <p className="text-xs text-muted-foreground">Producto</p>
            <p className="text-sm font-medium line-clamp-2">{productName}</p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <label className="text-xs font-semibold text-muted-foreground">
              {soldByWeight ? "Cantidad (kg)" : "Cantidad"}
            </label>
            <QuantityControl value={quantity} soldByWeight={soldByWeight} onChange={setQuantity} />
            {soldByWeight && (
              <span className="text-[11px] text-muted-foreground">Puedes escribir gramos, por ejemplo 0.300</span>
            )}
          </div>

          {loadState === "guest" && (
            <div className="rounded-xl border bg-primary/5 p-4 space-y-3">
              <p className="text-sm font-semibold">Inicia sesión para guardar productos</p>
              <p className="text-xs text-muted-foreground">
                Crea listas, compáralas entre tiendas y guarda tus productos favoritos.
              </p>
              <Button asChild className="w-full">
                <Link
                  href={`/auth/signin?callbackUrl=${encodeURIComponent(typeof window !== "undefined" ? window.location.pathname : "/")}`}
                >
                  <LogIn className="w-4 h-4 mr-1.5" /> Iniciar sesión
                </Link>
              </Button>
            </div>
          )}

          {loadState === "disabled" && (
            <p className="text-xs text-muted-foreground py-3">
              La función de listas no está disponible en este momento.
            </p>
          )}

          {(loadState === "ready" || loadState === "loading" || loadState === "error") && !creating && (
            <>
              <div className="space-y-1">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  Tus listas
                </p>
                {loadState === "loading" ? (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground py-3">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> Cargando...
                  </div>
                ) : loadState === "error" ? (
                  <p className="text-xs text-rose-600 py-3">No pudimos cargar tus listas. Reintenta más tarde.</p>
                ) : lists.length === 0 ? (
                  <p className="text-xs text-muted-foreground py-3">Aún no tienes listas. Crea la primera abajo.</p>
                ) : (
                  <ul className="rounded-xl border divide-y">
                    {lists.map((l) => (
                      <li key={l.id}>
                        <button
                          type="button"
                          onClick={() => addToList(l.id)}
                          disabled={submitting !== null}
                          className={cn(
                            "w-full flex items-center gap-3 px-3 py-2.5 hover:bg-muted/50 transition-colors text-left disabled:opacity-50"
                          )}
                        >
                          <span className="text-xl shrink-0">{l.emoji ?? "📋"}</span>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{l.name}</p>
                            <p className="text-[11px] text-muted-foreground">
                              {l.itemCount} {l.itemCount === 1 ? "artículo" : "artículos"}
                            </p>
                          </div>
                          {submitting === l.id ? (
                            <Loader2 className="w-4 h-4 animate-spin text-primary" />
                          ) : (
                            <Plus className="w-4 h-4 text-primary" />
                          )}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {loadState === "ready" && (
                <button
                  type="button"
                  onClick={() => setCreating(true)}
                  className="w-full text-sm font-medium text-primary inline-flex items-center justify-center gap-1 py-2 rounded-lg border-2 border-dashed border-primary/30 hover:bg-primary/5"
                >
                  <Plus className="w-4 h-4" /> Crear nueva lista
                </button>
              )}
            </>
          )}

          {creating && loadState === "ready" && (
            <div className="space-y-3 rounded-xl border p-3 bg-muted/20">
              <div>
                <label className="block text-xs font-semibold text-muted-foreground mb-1">Nombre de la lista</label>
                <input
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="Mi súper semanal"
                  maxLength={60}
                  className="w-full h-10 px-3 rounded-lg border border-input text-sm focus:outline-hidden focus:ring-2 focus:ring-ring"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-muted-foreground mb-1">Emoji</label>
                <div className="flex gap-1 flex-wrap">
                  {NEW_LIST_EMOJIS.map((e) => (
                    <button
                      key={e}
                      type="button"
                      onClick={() => setNewEmoji(e)}
                      className={cn(
                        "h-9 w-9 text-lg rounded border",
                        newEmoji === e && "bg-primary/10 border-primary"
                      )}
                    >
                      {e}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => setCreating(false)} disabled={!!submitting}>
                  Cancelar
                </Button>
                <Button
                  className="flex-1"
                  onClick={createAndAdd}
                  disabled={!newName.trim() || !!submitting}
                >
                  {submitting === "__new__" ? "Creando..." : "Crear y añadir"}
                </Button>
              </div>
            </div>
          )}

          {error && <p className="text-xs text-rose-600">{error}</p>}
          {success && (
            <p className="text-xs text-emerald-700 flex items-center gap-1">
              <Check className="w-3.5 h-3.5" /> {success}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
